import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { Loading } from '@src/common/Loading';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { config } from '@uhg-abyss/web/tools/config';
import { useTinRegistration } from '@src/state/tinRegistration';
import { event } from '@uhg-abyss/web/tools/event';
import isEmpty from 'lodash/isEmpty';
import { useUser } from '@src/state/user/hooks';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useTinDrafts } from '@src/state/superUserDraft';
import { BillingTin } from './Inputs/BillingTin';
import { Tin } from './Inputs/Tin';
import { OptumId } from './Inputs/OptumId';
import { DraftValidationModel } from './DraftValidation/DraftValidation';

const CardHeading = styled('p', {
  fontSize: '28px',
  color: '$primary1',
  fontWeight: 'bold',
  margin: 0,
});

const CardDescription = styled('p', {
  fontSize: '15px',
  lineHeight: '120%',
  fontWeight: 600,
  margin: '10px 0px',
  display: 'block',
});

export const TinRegistration = ({
  errorRes,
  setErrorRes,
  setIsAlertBanner,
  setTinRegError,
  setTinRegStatusError,
  onYes,
  populateDetails,
  setIsUserBlocked,
  setTimeRemaining,
}) => {
  const router = useRouter();
  const form = useFormV2();
  const userInfo = useUser.state();
  const isSuperUser = storage.local.get(userInfo.uuid);
  const {
    fetchTinStatus,
    setTrackingNumber,
    toggleReturn,
    disableSaveAsDraft,
  } = useTinRegistration.actions();
  const { tinDraftList, fetchTinStatusSaga } = useTinRegistration.state();
  const { isCheckoutUser } = userInfo;
  const registration = form.getValues('searchRegistration');
  const searchDraft = form.getValues('searchDraft');
  const tinDraft = Object.values(tinDraftList);

  const { retrieveSuperUserDraft } = useTinDrafts.actions();

  const [isDraftValidationModalOpen, setDraftValidationModalOpen] =
    useState(false);

  const closeDraftValidation = () => {
    setDraftValidationModalOpen(false);
  };

  const startNewRegistration = () => {
    toggleReturn(false);
    setIsAlertBanner(false);
    setIsUserBlocked(false);

    form.validate(
      'searchRegistration',
      (values) => {
        const { billingTin, requestId } = values;
        const updatedBillingTin = billingTin.replace(/-/g, '');
        setTinRegStatusError(false);
        fetchTinStatus(
          {
            billingTin: updatedBillingTin,
            requestId,
            test: isCheckoutUser,
          },
          (res) => {
            setTrackingNumber(res.trackingNumber);
            form.setValue('searchRegistration.billingTin', updatedBillingTin);
            event('TIN_REGISTRATION_START');
            router.navigate('/verify-tin');
          },
          (error) => {
            event('API_TRANSACTION_ERROR', {
              code: error.faultCode,
              message: error.message,
            });
            if (error.faultCode === 'EPTSUB' && error.timestamp) {
              const ms = Date.parse(error.timestamp) - Date.now();
              setTimeRemaining(ms);
              setIsUserBlocked(true);
            } else {
              setErrorRes({ error });
              setIsAlertBanner(true);
              setTinRegStatusError(false);
            }

            if (
              errorRes.error.statusCode === '500' ||
              errorRes.error.statusCode === '401'
            ) {
              setTinRegError(true);
            }
          }
        );
      },
      (error) => {
        const fields = {};
        if (isEmpty(error.billingTin)) {
          fields.billingTin = '';
        }
        if (!isEmpty(fields)) {
          event('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  const handleStartRegistration = () => {
    if (isSuperUser) {
      const { tin, optumId } = searchDraft;
      form.validate('searchRegistration', (values) => {
        retrieveSuperUserDraft(
          {
            billingTin: tin,
            optumId,
          },
          (res) => {
            setTrackingNumber(res.trackingNumber);
            form.setValue('searchRegistration.billingTin', tin);
            form.setValue('searchRegistration.optumId', optumId);
            form.setValue('superUserDraft', res);
            populateDetails(res);
          },
          (error) => {
            setTinRegStatusError(true);
          }
        );
      });
    } else if (
      tinDraft &&
      tinDraft.length &&
      tinDraft.some((element) => {
        return element.billingTin === registration.billingTin;
      })
    ) {
      tinDraft.forEach((element) => {
        if (element && element.billingTin === registration.billingTin) {
          disableSaveAsDraft(false);
          onYes(element);
        }
      });
    } else {
      startNewRegistration();
    }
  };

  const checkDraft = () => {
    if (tinDraft != null && tinDraft.length === 5) {
      disableSaveAsDraft(true);
      setDraftValidationModalOpen(true);
    } else {
      disableSaveAsDraft(false);
      handleStartRegistration();
    }
  };
  const isSuperUserFlag = isSuperUser === true || isSuperUser === 'true';
  const isStartDisabled = config('US3671382_SUPER_USER') && isSuperUserFlag;

  return (
    <Loading isLoading={fetchTinStatusSaga.isLoading}>
      <DraftValidationModel
        isOpen={isDraftValidationModalOpen}
        closeModal={closeDraftValidation}
        handleStartRegistration={handleStartRegistration}
      />

      {isSuperUser ? (
        <V2Card>
          <CardHeading>
            Search for Taxpayer Identification Number (TIN)
          </CardHeading>
          <CardDescription id="dashboard-tinRegistrationText">
            Begin the verification process by entering the TIN used for claims
            billing.
          </CardDescription>
          <Grid>
            <Grid.Col span="50%">
              <Tin />
            </Grid.Col>
            <Grid.Col span="50%">
              <OptumId />
            </Grid.Col>
          </Grid>
          <V2Button
            id="search-draft-button"
            variant="filled"
            aria-label="click here to Search Billing TIN Draft"
            data-testid="search-button"
            onClick={handleStartRegistration}
            css={{ marginTop: '18px' }}
          >
            Search
          </V2Button>
        </V2Card>
      ) : (
        <V2Card>
          <CardHeading>Verify your TIN</CardHeading>
          <CardDescription id="dashboard-tinRegistrationText">
            Begin the verification process by entering the TIN used for claims
            billing.
          </CardDescription>
          <BillingTin />
          <V2Button
            id="startregistration-button"
            variant="filled"
            aria-label="click here to Register Billing TIN"
            data-testid="start-button"
            onClick={checkDraft}
            css={{ marginTop: '16px' }}
            isDisabled={isStartDisabled ? true : undefined}
          >
            Start now
          </V2Button>
        </V2Card>
      )}
    </Loading>
  );
};

TinRegistration.propTypes = {
  setErrorRes: PropTypes.func.isRequired,
  errorRes: PropTypes.shape({
    error: PropTypes.shape({
      statusCode: PropTypes.number,
      faultCode: PropTypes.string,
      message: PropTypes.string,
    }),
  }).isRequired,
  setIsAlertBanner: PropTypes.func.isRequired,
  setTinRegError: PropTypes.func.isRequired,
  setTinRegStatusError: PropTypes.func.isRequired,
  onYes: PropTypes.func.isRequired,
  populateDetails: PropTypes.func.isRequired,
  setIsUserBlocked: PropTypes.func.isRequired,
  setTimeRemaining: PropTypes.func.isRequired,
};

TinRegistration.defaultProps = {};
